import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { parseCMSBold, sendEvent, useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";

import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { CameraControls, Environment, Float, Html, Image, OrbitControls, PerspectiveCamera, Stats } from "@react-three/drei";
import SectionHeader from "@components/SectionHeader";
import ButtonsNav from "./ButtonsNav";
import { RGBELoader } from "three-stdlib";
import Input from "@components/Input";
import { useForm } from "react-hook-form";
import Switch from "@components/Switch";
import * as THREE from 'three'
import { showErrorPopup, translateError } from "@components/ErrorHandling";
import { useSpring, animated, config } from "@react-spring/three";
import { VuseUltraBlue } from "./VuseUltraBlue";
import Gyro from "./img/gyro.svg";
import Mouse from "./img/mouse.svg";
import PrevFeature from "./img/prev-feature.png"
import NextFeature from "./img/next-feature.png"
import CloseFeature from "./img/close-feature.png"
import Orb1 from "./img/orb1.png";
import Orb2 from "./img/orb2.png";
import Orb3 from "./img/orb3.png";
import BlueThumb from "./img/blue-thumb.png";
import BlackThumb from "./img/black-thumb.png";
import PinkThumb from "./img/pink-thumb.png";
import MarkerCircle from "./img/mark.png"
import MarkerFeatureImg from "./img/marker-feature.png"
import MarkerChecked from "./img/checked.png"
import { DoubleSide } from 'three'
import { lerp } from "three/src/math/MathUtils";
import { VuseUltraScreen } from "./VuseUltraScreen";
import ColorItemBg from "./img/color-item-bg.svg?react"


import FeatureIcon0 from "./img/icon-feature-0.png"
import FeatureIcon1 from "./img/icon-feature-1.svg"
import FeatureIcon2 from "./img/icon-feature-2.svg"
import FeatureIcon3 from "./img/icon-feature-3.png"
import FeatureIcon4 from "./img/icon-feature-4.svg"
import FeatureIcon5 from "./img/icon-feature-5.svg"

const AnimatedImage = animated(Image)

export default function VuseUltra3D({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
  detailsQuery,
  refetchActivation,
  setContestAnswerSended,
  setUserPayload,
  data,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const [selectedColor, setSelectedColor] = useState(0)
  const [step, setStep] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState(-1)
  const [loading, setLoading] = useState(false);
  const [lockCamera, setLockCamera] = useState(false)
  const controls = useRef(null)
  const XGA = width >= 1020;
  const [checkedFeatures, setCheckedFeatures] = useState([])
  const [showTooltip, setShowTooltip] = useState(true)
  
  const featureList = [
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon0} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>СLEARVIEW DISPLAY</h2>
          <h4>Echtzeit Dampf-Tracking</h4>
          <p>Behalte Füllstand, Akkustand und Flavour-Intensität dank integriertem AMOLED-Display jederzeit im Blick – für volle Kontrolle über dein Vuse Ultra.</p>
        </div>
      </>,
      cameraPosition: [0, -1, 2],
      cameraLookAt: [-3.2, -1, -7],
      markerPosition: [-1, 1.6, 1],
      markerFeaturePosition: [-0.8, 1.9, 1.5],
      markerFeatureScale: 1.4,
      uuid: "f33e28d6-39b7-4b07-a0d4-91dc16c91d29",
      screen: "drop"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon1} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>Flavour Autotune</h2>
          <h4>Jedes Mal der perfekte Geschmack</h4>
          <p>Flavour AutoTune erkennt deinen ultra Flavour und optimiert automatisch Leistung und Intensität deines Vuse Ultra. Dank Smart Chip in den Ultra Pods merkt sich Vuse Ultra deine Einstellungen.</p>
        </div>
      </>,
      cameraPosition: [0, 1, 2.2],
      cameraLookAt: [-0.7, 1.5, 0],
      markerPosition: [-1.2, 7, 1],
      markerFeaturePosition: [-0.8, 7, 1.5],
      markerFeatureScale: 1.7,
      uuid:   "1ae2fa6d-810b-4698-82f9-4643374846f4",
      screen: "drop"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon2} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>MULTIFUNKTIONSTASTE</h2>
          <h4>Intuitive Steuerung immer zur Hand</h4>
          <p>Wechsle über die Multifunktionstaste auf dem AMOLED-Display zwischen Liquid-Füllstand, Flavour-Intensität und Akkustand. Durch 3x schnelles Drücken der Taste kannst du dein Gerät bequem sperren und entsperren.</p>
        </div>
      </>,
      cameraPosition: [-3, 0.5, 1],
      cameraLookAt: [-1, -1, 0],
      markerPosition: [-2.2, 2.4, 0.9],
      markerFeaturePosition: [-3.2, 3, 0.5],
      markerFeatureScale: 1.5,
      uuid:   "0299f70c-36a5-4039-98ab-132f11d9902e",
      screen: "drop"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon3} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>Advanced charging</h2>
          <h4>Wireless Charging oder mit USB-C schnell aufladen</h4>
          <p>Jetzt noch einfacher und bequemer Aufladen mit der Wireless Charging Funktion. Mit USB-C kannst du das Device auf 80% in 20 Minuten oder auf 100% in 35 Minuten aufladen.</p>
        </div>
      </>,
      cameraPosition: [-1.5, -4, 1],
      cameraLookAt: [-0.5, -1, 0],
      markerPosition: [-1, 0, 1],
      markerFeaturePosition: [-1.2, -0.4, 0],
      markerFeatureScale: 1.3,
      uuid:   "4bdbb4ff-7f84-4ba8-8f80-4a2844ebefd9",
      screen: "battery"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon4} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>Längere Akkulaufzeit</h2>
          <h4>530 mAh Kapazität</h4>
          <p>Bleibt länger mit Energie versorgt dank des wiederaufladbaren Akkus mit über 40 % höherer Kapazität im Vergleich zum Vuse PRO Smart.</p>
        </div>
      </>,
      cameraPosition: [1.5, -4, 2],
      cameraLookAt: [0.3, -1.5, 0],
      markerPosition: [1.3, 0, 1],
      markerFeaturePosition: [1.5, -0.8, 1],
      markerFeatureScale: 1.7,
      uuid:   "131b2b1f-2f32-4489-9749-d3198957ebb2",
      screen: "battery"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon5} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>BLUETOOTH-UND HAPTIK-FUNKTIONEN</h2>
          <h4>Mit haptischem Feedback</h4>
          <p>Per Bluetooth verbindest du Vuse Ultra mit der MYVUSE App für ein personalisiertes Erlebnis. Dank Haptik spürst du, wenn Liquid oder Akku zur Neige gehen.</p>
        </div>
      </>,
      cameraPosition: [0.1, -1, 2.5],
      cameraLookAt: [-3.2, -1, -7],
      markerPosition: [-0.4, 4, 1.1],
      markerFeaturePosition: [-0.8, 1.9, 1.5],
      markerFeatureScale: 1.5,
      uuid:   "10f2ad25-9597-42bf-9629-8fd14411e965",
      screen: "bluetooth"
    },
    {
      content: <>
        <div className="vuse-feature-icon">
          <img src={FeatureIcon2} alt=""/>
        </div>
        <div className="vuse-feature-description">
          <h2>CLOUDCONTROL-TASTE</h2>
          <h4>Intensität nach deinem Geschmack</h4>
          <p>Mit CloudControl kann die Flavour-Intensität gewählt werden, die gewünscht ist. Der Wechsel zwischen niedrigen, mittleren und hohen Intensitätsstufen geht über das Vuse Ultra Device.</p>
        </div>
      </>,
      cameraPosition: [3, 0.5, 0.5],
      cameraLookAt: [-2, -1, 0],
      markerPosition: [2.2, 4, 0.9],
      markerFeaturePosition: [3, 4.4, 0.2],
      markerFeatureScale: 1.7,
      uuid:   "0299f70c-36a5-4039-98ab-132f11d9902f",
      screen: "battery"
    }
  ]

  const deviceColors = [
    {
      thumb: BlackThumb,
      orb: Orb1
    },
    {
      thumb: BlueThumb,
      orb: Orb2
    },
    {
      thumb: PinkThumb,
      orb: Orb3
    }
  ]

  const isMobile = () => {
    let check = false;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const onSubmit = (data) => {
    const payload = {
      activation: {
        ident: ident,
        payload: [{
            "question_ident": "q0",
            "answer_ident": [
              [
                "f33e28d6-39b7-4b07-a0d4-91dc16c91d29",
                "1ae2fa6d-810b-4698-82f9-4643374846f4",
                "0299f70c-36a5-4039-98ab-132f11d9902e",
                "4bdbb4ff-7f84-4ba8-8f80-4a2844ebefd9",
                "131b2b1f-2f32-4489-9749-d3198957ebb2",
                "10f2ad25-9597-42bf-9629-8fd14411e965"
              ]
            ]
        }]
      }
    };

    fetch(import.meta.env.VITE_API_URL + "/activation/solution/create", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        return response.json();
      })
      .then(
        (result) => {
          if (result.status.success) {
            detailsQuery.refetch();
            refetchActivation();
            sendEvent(`${ident}_FINISH`);
            setUserPayload(result?.data)
            setContestAnswerSended(true);
          } else {
            showErrorPopup(
              translateError(result.data.error, result.meta.id),
              result.meta.ts
            );
          }
          // setSending(false)
        },
        (error) => {
          console.log(error);
          // setSending(false)
        }
      );
  };

  const texture = useLoader(RGBELoader, "/data/potsdamer_platz_1k.hdr");
  // texture.mapping = THREE.EquirectangularReflectionMapping

  function Marker({checked, action, ...props}) {
    const ref = useRef(null)
    const {camera} = useThree()

    useFrame(({state}) => {
        if(ref) {
            ref.current.rotation.set(camera.rotation._x, camera.rotation._y, 0)
        }

    })

    return (
        <group ref={ref} {...props} scale={0.65}>
            { checked ? <Image
              url={MarkerChecked} 
              transparent
              side={DoubleSide}
              onClick={action}
            /> : <Image
              url={MarkerCircle} 
              transparent
              side={DoubleSide}
              onClick={action}
          /> }
        </group>
    )
  }

  function MarkerFeature({...props}) {
    const ref = useRef(null)
    const {camera} = useThree()

    useFrame(({state}) => {
        if(ref) {
            ref.current.rotation.set(camera.rotation._x, camera.rotation._y, 0)
        }
    })

    return (
        <group ref={ref} {...props}>
            <Image
              url={MarkerFeatureImg} 
              transparent
              side={DoubleSide}
            /> 
        </group>
    )
  }

  const cameraProperties = useMemo(
      () => ({
          position: new THREE.Vector3(0, 0, 10),
          target: new THREE.Vector3()
      }),
      []
  )

  const springBlack = useSpring({opacity: selectedColor === 0 ? 1 : 0, config: config.stiff, delay: selectedColor !== 0 ? 100 : 0});
  const springBlue = useSpring({opacity: selectedColor === 1 ? 1 : 0, config: config.stiff, delay: selectedColor !== 1 ? 100 : 0});
  const springPink = useSpring({opacity: selectedColor === 2 ? 1 : 0, config: config.stiff, delay: selectedColor !== 2 ? 100 : 0});

  const ACTION = {
    NONE: 0,
    ROTATE: 1,
    TRUCK: 2,
    OFFSET: 4,
    DOLLY: 8,
    ZOOM: 16,
    TOUCH_ROTATE: 32,
    TOUCH_TRUCK: 64,
    TOUCH_OFFSET: 128,
    TOUCH_DOLLY: 256,
    TOUCH_ZOOM: 512,
    TOUCH_DOLLY_TRUCK: 1024,
    TOUCH_DOLLY_OFFSET: 2048,
    TOUCH_DOLLY_ROTATE: 4096,
    TOUCH_ZOOM_TRUCK: 8192,
    TOUCH_ZOOM_OFFSET: 16384,
    TOUCH_ZOOM_ROTATE: 32768
  }

  useEffect(() => {
    const position = featureList[selectedFeature]?.cameraPosition || [0, 0, 5]
    const target = featureList[selectedFeature]?.cameraLookAt || [0, 0, 0]

    let tempCheckedFeatures = [...checkedFeatures]
    if(featureList[selectedFeature]?.uuid && !tempCheckedFeatures.includes(featureList[selectedFeature]?.uuid)) tempCheckedFeatures.push(featureList[selectedFeature].uuid)
    setCheckedFeatures(tempCheckedFeatures)

    controls.current?.setLookAt(position[0], position[1], position[2], target[0], target[1], target[2], true);
  }, [selectedFeature])

  useEffect(() => {
    setTimeout(() => { setShowTooltip(false) }, 8000)
  }, [])

  const Orbs = ({selectedFeature}) => {
    const groupRef = useRef(null)

    // useFrame(() => {
    //   if(groupRef.current) {
    //     // console.log(groupRef.current)
    //     groupRef.current.scale.x = lerp(groupRef.current.scale.x, XGA ? (selectedFeature !== -1 ? 30 : 20) : (selectedFeature !== -1 ? 16 : 12), 0.05)
    //     groupRef.current.scale.y = lerp(groupRef.current.scale.y, XGA ? (selectedFeature !== -1 ? 30 : 20) : (selectedFeature !== -1 ? 16 : 12), 0.05)
    //     groupRef.current.scale.z = lerp(groupRef.current.scale.z, XGA ? (selectedFeature !== -1 ? 30 : 20) : (selectedFeature !== -1 ? 16 : 12), 0.05)
    //   }
    // })

    return (
      <group ref={groupRef} scale={XGA ? 20 : 12} position={[2, 0, -5]}>
        <AnimatedImage url={deviceColors[0].orb} transparent material-transparent material-opacity={springBlack.opacity}/>
        <AnimatedImage url={deviceColors[2].orb} transparent material-transparent material-opacity={springBlue.opacity}/>
        <AnimatedImage url={deviceColors[2].orb} transparent material-transparent material-opacity={springPink.opacity}/>
      </group>
    )
  }
  
  const memoOrbs = useMemo(() => <Orbs selectedFeature={selectedFeature}/>, [selectedFeature])

  const deviceMemo = useMemo(() => <VuseUltraBlue 
    color={selectedColor === 2 ? "pink" : selectedColor === 1 ? "blue" : "black"}
  />, [selectedColor])

  return (
    <section className={`game-page quiz-mechanics vuse-ultra-3d`}>
      <div>
        <PagesHeader back>
          <p>Lerne das neue ULTRA von Vuse kennen</p>
        </PagesHeader>
        <div 
          className="main-wrapper"
        >
          {step === 0 ? <h4 className="vuse-ultra-header">Wähle deine <br/><strong>Lieblingsfarbe</strong></h4> : step === 1 ? <h4 className="vuse-ultra-header">Entdecke die <br/><strong>wichtigsten Funktionen</strong></h4> : <h4 className="vuse-ultra-header">Sichere dir als Erster dein <br/><strong>Vuse Ultra</strong></h4> }
          <Suspense fallback={<h4 className="vuse-loading">Loading...</h4>}>
            <Canvas
              id="scene"
              dpr={1.5}
            >
              <Environment
                preset="warehouse"
                environmentIntensity={selectedColor === 0 ? 3 : 1.5}
                environmentRotation={[Math.PI/6, Math.PI/20, 0]}
              />
              <ambientLight intensity={1} />
              <directionalLight position={[-5, 2, 5]} intensity={1}/>
              <Float speed={0.4}>
                <group scale={0.5} position={[0, -2, 0]}>
                  { deviceMemo }
                  <VuseUltraScreen type={featureList[selectedFeature]?.screen || "drop"}/>
                  { selectedFeature === -1 && step === 1 && featureList.map((item, i) => {
                    return <Marker key={i} checked={checkedFeatures.includes(item.uuid)} position={item.markerPosition} action={() => { 
                      setSelectedFeature(i)
                    }}/>
                  }) }
                  { selectedFeature !== -1 && <MarkerFeature position={featureList[selectedFeature]?.markerFeaturePosition} scale={featureList[selectedFeature]?.markerFeatureScale}/> }
                  {/* <CameraHelper selectedFeature={selectedFeature} lockCamera={lockCamera}/> */}
                </group>
              </Float>
              <CameraControls 
                ref={controls}
                enabled={true}
                mouseButtons={selectedFeature !== -1 ? { left: ACTION.NONE, middle: ACTION.NONE, right: ACTION.NONE, wheel: ACTION.NONE } : { left: ACTION.ROTATE, middle: ACTION.NONE, right: ACTION.NONE, wheel: ACTION.NONE }}
                touches={selectedFeature !== -1 ? { one: ACTION.NONE, two: ACTION.NONE, three: ACTION.NONE } :  { one: ACTION.TOUCH_ROTATE, two: ACTION.NONE, three: ACTION.NONE }}
              />
              {/* <Stats/> */}
            </Canvas>
            <Canvas
              id="scene2"
              className={selectedFeature !== -1 ? "blurred" : ""}
              dpr={1.5}
            >
              <Environment
                preset="studio"
                environmentIntensity={1.5}
              />
              <ambientLight intensity={1} />
              <Float speed={0.4}>
                {/* <Orbs selectedFeature={selectedFeature}/> */}
                { memoOrbs }
              </Float>
            </Canvas>
          </Suspense>
          { step === 0 && <div className="color-selector">
            {deviceColors.map((item, i) => {
              return (
                <div className={`color-item ${selectedColor === i ? "active" : ""}`} key={i} onClick={() => { setSelectedColor(i) }}>
                  <img src={item.thumb} alt=""/>
                  <ColorItemBg className="color-item-bg"/>
                </div>
              )
            })}
          </div> }
         { selectedFeature !== -1 && <div className="close-feature" onClick={() => { 
              setSelectedFeature(-1)
              controls.current?.setLookAt(0, 0, 5, 0, 0, 0, true);
            }}>
            <img src={CloseFeature} alt="close feature"/>
          </div> }
          { selectedFeature !== -1 && <div className={`selected-feature ${checkedFeatures.length >=6 ? "to-top" : ""}`}>
            { featureList[selectedFeature]?.content }
          </div> }
          { selectedFeature !== -1 && <div className="prev-next-buttons">
            <div className="prev" onClick={() => { selectedFeature > 0 ? setSelectedFeature(selectedFeature-1) : setSelectedFeature(featureList.length-1) }}>
              <img src={PrevFeature} alt=""/>
            </div>
            <div className="next" onClick={() => { selectedFeature < featureList.length-1 ? setSelectedFeature(selectedFeature+1) : setSelectedFeature(0) }}>
              <img src={NextFeature} alt=""/>
            </div> 
          </div> }
          { step === 2 && <div className={`selected-feature ${checkedFeatures.length >= 6 ? "to-top" : ""}`}>
            <div className="vuse-feature-description">
              <h2>NEU ULTRA. BY VUSE</h2>
              <h4>Sicher dir jetzt das neue Vuse Ultra Device.</h4>
              <p>Entdecke Ultra mit neuen Ultra Pods und Flavours – personalisiert für dich. Das fortschrittlichste Vaping-Erlebnis jetzt mit 30 Tagen Geld-zurück-Garantie. Für noch mehr Kontrolle: Kopple dein Vuse Ultra optional mit der MYVUSE App.</p>
            </div>
          </div> }
          { step === 0 && <Button btnClass="outline yellow" action={() => { setStep(1) }}>Mein Favorit!</Button> }
          { checkedFeatures.length >=7 && <Button btnClass="outline yellow" action={() => { 
            if(step === 2) {
              onSubmit()
            } else {
              setSelectedFeature(-1); 
              setStep(2) 
            }
            }}>Nächste</Button>}
          <div
            className={
              !showTooltip || step !== 0 ? "short-instruction hide" : "short-instruction"
            }
            onClick={() => setShowTooltip(false)}
          >
            <img src={isMobile() ? Gyro : Mouse} alt="Mouse-icon" />
            {isMobile() ? (
              <p>
                Zum Zoomen zusammen- und <br/>
                auseinanderziehen, mit den <br/>
                Fingern drehen.
              </p>
            ) : (
              <p>
                Zum Vergrößern oder Verkleinern <br/>
                scrollen. Verwende die Maus, um <br/>
                das Gerät zu drehen.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
